// Save-to-brain distillation (wave-2 W2). The model proposes 1-3 thoughts as an
// ```agent-proposals JSON block; the human edits and saves each one. The model
// is NOT in the write path — Save calls the same audited capture endpoint the
// dashboard uses. Any parse failure yields no panel (degrades silently).

import { useState } from "react";
import { Icon } from "../design/ui";
import { captureThought } from "../lib/brain-api";
import type { Scope } from "../lib/brain-types";
import "./agent.css";

interface Proposal {
  text: string;
  type?: string;
  scope?: Scope;
}

const TYPES = ["observation", "idea", "reference", "person_note", "task"];

/** Extract the first ```agent-proposals fenced block as an array, or null. */
export function parseProposals(text: string): Proposal[] | null {
  const m = /```agent-proposals\s*([\s\S]*?)```/.exec(text);
  if (!m) return null;
  try {
    const arr = JSON.parse(m[1].trim());
    if (Array.isArray(arr) && arr.every((p) => p && typeof p.text === "string")) return arr;
  } catch { /* malformed — no panel */ }
  return null;
}

type RowState = { text: string; type: string; scope: Scope; saved?: string; busy?: boolean; error?: boolean };

export function ProposalPanel({ proposals }: { proposals: Proposal[] }) {
  const [rows, setRows] = useState<RowState[]>(
    proposals.map((p) => ({
      text: p.text,
      type: p.type && TYPES.includes(p.type) ? p.type : "observation",
      scope: p.scope === "company" ? "company" : "personal",
    })),
  );

  const patch = (i: number, next: Partial<RowState>) =>
    setRows((r) => r.map((row, j) => (j === i ? { ...row, ...next } : row)));

  const save = async (i: number) => {
    const row = rows[i];
    patch(i, { busy: true, error: false });
    try {
      await captureThought(row.text, row.scope, row.type);
      patch(i, { busy: false, saved: "saved" });
    } catch {
      patch(i, { busy: false, error: true });
    }
  };

  return (
    <div className="agent-proposals">
      <div className="agent-proposals-head">
        <Icon name="bookmark" size={13} /> Save to brain
      </div>
      {rows.map((row, i) => (
        <div key={i} className="agent-proposal">
          {row.saved ? (
            <div className="agent-proposal-saved"><Icon name="check" size={13} /> Saved to {row.scope}</div>
          ) : (
            <>
              <textarea
                className="agent-proposal-text"
                value={row.text}
                rows={2}
                onChange={(e) => patch(i, { text: e.target.value })}
                aria-label="Thought text"
              />
              <div className="agent-proposal-foot">
                <select className="agent-proposal-select" value={row.type} onChange={(e) => patch(i, { type: e.target.value })} aria-label="Type">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="agent-proposal-select" value={row.scope} onChange={(e) => patch(i, { scope: e.target.value as Scope })} aria-label="Scope">
                  <option value="personal">personal</option>
                  <option value="company">company</option>
                </select>
                <button type="button" className="agent-proposal-save" disabled={row.busy || !row.text.trim()} onClick={() => void save(i)}>
                  {row.busy ? "Saving…" : "Save"}
                </button>
              </div>
              {row.error && <div className="agent-error">Save failed — try again.</div>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
